import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import { CSSProperties, useEffect, useMemo, useRef, useState } from 'react'

import { Coordinates } from '../../outlets/types'
import { MapMouseEventHandler, MapViewMarkerConfig } from '../types'

import styles from './MapView.module.scss'

const DefaultZoom = 14

/** Delivery area polygon styling */
// TODO: Get values from generated theme
const PolygonOptions: google.maps.PolygonOptions = {
  strokeColor: '#00FF00',
  fillColor: '#00FF00',
  strokeOpacity: 0.5,
  strokeWeight: 2,
  fillOpacity: 0.25,
}

interface MapViewProps {
  /** Points of the delivery area polygon, drawn when provided */
  polygonPoints?: Coordinates[]
  /** Position of the marker, also used as the map center */
  markerCoordinates?: Coordinates
  markerConfig?: MapViewMarkerConfig
  onClickPolygon?: MapMouseEventHandler
  /** Overrides the default absolute, full size container */
  containerStyle?: CSSProperties
  zoom?: number
  gestureHandling?: google.maps.MapOptions['gestureHandling']
  fullscreenControl?: boolean
}

/**
 * Renders a Google Map with an optional marker and delivery area polygon
 * directly on the Google Maps JavaScript API.
 *
 * The API script must already be loaded (see `useGoogleMapsScript`), so render
 * this component only once the script status is `ScriptStatus.Ready`.
 */
export default function MapView({
  polygonPoints,
  markerCoordinates,
  markerConfig,
  onClickPolygon,
  containerStyle,
  zoom = DefaultZoom,
  gestureHandling,
  fullscreenControl,
}: MapViewProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map>()
  const [marker, setMarker] = useState<google.maps.Marker>()
  const [polygon, setPolygon] = useState<google.maps.Polygon>()

  const {
    draggable,
    icon,
    optimized,
    onDragend: onMarkerDragend,
  } = markerConfig ?? {}

  const markerLatitude = markerCoordinates?.latitude
  const markerLongitude = markerCoordinates?.longitude

  const markerPosition = useMemo<google.maps.LatLngLiteral | undefined>(() => {
    if (markerLatitude === undefined || markerLongitude === undefined) {
      return undefined
    }
    return { lat: markerLatitude, lng: markerLongitude }
  }, [markerLatitude, markerLongitude])

  const polygonPaths = useMemo<google.maps.LatLngLiteral[] | undefined>(
    () =>
      polygonPoints?.map(point => ({
        lat: point.latitude,
        lng: point.longitude,
      })),
    [polygonPoints],
  )

  const hasMarker = !!markerPosition
  const hasPolygon = !!polygonPaths

  // Create the map once the container is mounted
  useEffect(() => {
    const container = mapContainerRef.current

    if (!container) return undefined

    const newMap = new window.google.maps.Map(
      container,
      omitBy<google.maps.MapOptions>(
        {
          center: markerPosition,
          zoom,
          streetViewControl: false,
          gestureHandling,
          fullscreenControl,
        },
        isUndefined,
      ),
    )

    setMap(newMap)

    return () => {
      setMap(undefined)
    }
    // The map is created once; option changes are applied by the effects below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keep map options in sync
  useEffect(() => {
    map?.setOptions(
      omitBy<google.maps.MapOptions>(
        {
          zoom,
          gestureHandling,
          fullscreenControl,
        },
        isUndefined,
      ),
    )
  }, [map, zoom, gestureHandling, fullscreenControl])

  // Follow the marker position
  useEffect(() => {
    if (map && markerPosition) {
      map.panTo(markerPosition)
    }
  }, [map, markerPosition])

  // Create and remove the marker
  useEffect(() => {
    if (!map || !hasMarker) return undefined

    const newMarker = new window.google.maps.Marker({ map })

    setMarker(newMarker)

    return () => {
      newMarker.setMap(null)
      setMarker(undefined)
    }
  }, [map, hasMarker])

  // Keep marker options in sync
  useEffect(() => {
    if (marker && markerPosition) {
      // Google Maps rejects explicit `undefined` values (e.g. `setIcon`)
      marker.setOptions(
        omitBy<google.maps.MarkerOptions>(
          {
            position: markerPosition,
            draggable,
            icon,
            optimized,
          },
          isUndefined,
        ),
      )
    }
  }, [marker, markerPosition, draggable, icon, optimized])

  // Marker drag end listener
  useEffect(() => {
    if (!marker || !onMarkerDragend) return undefined

    const listener = marker.addListener('dragend', onMarkerDragend)

    return () => {
      listener.remove()
    }
  }, [marker, onMarkerDragend])

  // Create and remove the polygon
  useEffect(() => {
    if (!map || !hasPolygon) return undefined

    const newPolygon = new window.google.maps.Polygon({
      ...PolygonOptions,
      map,
    })

    setPolygon(newPolygon)

    return () => {
      newPolygon.setMap(null)
      setPolygon(undefined)
    }
  }, [map, hasPolygon])

  // Keep polygon paths in sync
  useEffect(() => {
    if (polygon && polygonPaths) {
      polygon.setPaths(polygonPaths)
    }
  }, [polygon, polygonPaths])

  // Polygon click listener
  useEffect(() => {
    if (!polygon || !onClickPolygon) return undefined

    const listener = polygon.addListener('click', onClickPolygon)

    return () => {
      listener.remove()
    }
  }, [polygon, onClickPolygon])

  return (
    <div className={styles.container} style={containerStyle}>
      <div ref={mapContainerRef} className={styles.map} />
    </div>
  )
}
